import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { ComponentType } from '@angular/cdk/overlay';
import { Directive, ElementRef, HostListener, Input, OnDestroy, TemplateRef } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { PopoverRef } from './popover-ref';
import { PopoverService } from './popover.service';
import { PopoverTemplateComponent } from './template/template.component';

@Directive({
  selector: '[sgtPopover]',
})
export class PopoverDirective implements OnDestroy {
  @Input('sgtPopoverTitle') title = '';
  @Input('sgtPopoverData') data: any = {};
  @Input('sgtPopoverDescribeBy') describeBy = '';
  @Input('sgtPopoverTemplate') componentOrTemplate: ComponentType<PopoverTemplateComponent> | TemplateRef<PopoverTemplateComponent> =
    PopoverTemplateComponent;
  @Input('sgtPopoverEvent') sgtPopoverEvent: 'hover' | 'click' = 'hover';

  @HostListener('mouseenter', ["$event"]) onMouseEnter(mouseEvent: MouseEvent): void {
    this.mouse$.next('mouseenter');
    mouseEvent.stopPropagation();
  }

  @HostListener('mouseleave', ["$event"]) onMouseLeave(mouseEvent: MouseEvent): void {
    this.mouse$.next(null);
    mouseEvent.stopPropagation();
  }

  @HostListener('click', ["$event"]) onClick(mouseEvent: MouseEvent): void {
    if (this.sgtPopoverEvent === 'click') {
      this.openPopOver();
      mouseEvent.stopPropagation();
    }
    this.hasFocus = false;
  }

  /** Emits when the component is destroyed. */
  private readonly _destroyed = new Subject<void>();
  private readonly mouse$ = new Subject<string | null>();

  popoverReference: PopoverRef<unknown>;
  isVisible = false;
  hasFocus = false;

  constructor(
    private readonly popoverService: PopoverService,
    private readonly elementRef: ElementRef,
    private readonly focusMonitor: FocusMonitor,
    private readonly describer: AriaDescriber
  ) {
    merge(
      this.focusMonitor.monitor(elementRef).pipe(
        tap((ev) => {
          this.hasFocus = !!ev;
        })
      ),
      this.mouse$.pipe(filter(() => !this.hasFocus))
    )
      .pipe(filter(() => this.sgtPopoverEvent === 'hover' || this.sgtPopoverEvent === 'click'))
      .pipe(takeUntil(this._destroyed))
      .subscribe((ev: string) => this.handleEvents(ev));
  }

  private openPopOver(): PopoverRef<any> {
    const { config } =  this.data;
    return this.popoverService.open(this.componentOrTemplate, this.elementRef, {
      data: this.data,
      hasBackdrop: false,
      ...config
    });
  }

  private handleEvents(ev: string): void {
    if (ev && !this.isVisible) {
      this.isVisible = true;
      this.describer.describe(this.elementRef.nativeElement, this.describeBy ? this.data[this.describeBy] : this.data, 'tooltip');
      this.popoverReference = this.openPopOver();
    } else if (!ev && this.isVisible) {
      this.isVisible = false;
      this.popoverReference.close();
    }
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }
}
